import React, {
  startTransition,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import { Button, Col, Container, Form, InputGroup, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import {
  calculateKelaPension,
  type KelaCalculationContext,
} from "../../calculation/KelaCalculation";
import {
  calculateOve,
  type OveCalculationContext,
} from "../../calculation/OveCalculation";
import {
  type AccruedPensions,
  accruedPensionsValue,
  type IndexData,
  type PensionEstimate,
  type YearConstants,
} from "../../types/Codes";
import type {
  KelaModel,
  KelaPensionCalculationResult,
} from "../../types/KelaModel";
import type { OveModel, OveResult } from "../../types/OveModel";
import { calculateTargetDateForNextMonth } from "../../utils/ageUtils";
import { rangeClosed } from "../../utils/array-utils";
import { getBirthYearOptions } from "../../utils/birthYearUtils";
import { getMonthName, scrollTo } from "../../utils/form-util";
import { localeEuroSumOrZero, toPercentage } from "../../utils/numberUtils";
import { trackEvent } from "../../utils/piwik-util";
import { parseQuery } from "../../utils/queryUtils";
import CalculationResult from "../CalculationResult";
import CodesContext from "../CodesContext";
import HelpTextAccordion from "../HelpTextAccordion";
import NumberInput from "../NumberInput";
import RadioInput from "../RadioInput";
import ResultsGroup from "../ResultsGroup";
import { useCodeList } from "../useCodeList";
import { useValidation } from "../useValidation";
import { useValidationOnLanguageChange } from "../useValidationOnLanguageChange";
import ValidationContext, { type Validation } from "../ValidationContext";
import ValidationFeedback from "../ValidationFeedback";
import LargeSelectInput from "../LargeSelectInput";

const OveCalculator: React.FC = () => {
  const yearConstants = useCodeList<YearConstants>("year-constants");
  const pensionEstimate = useCodeList<PensionEstimate>("pension-estimate");
  const index = useCodeList<IndexData>("index");
  const accruedPensions = useCodeList<AccruedPensions>("accrued-pensions");
  return (
    <CodesContext.Provider
      value={{ yearConstants, pensionEstimate, index, accruedPensions }}
    >
      <OveView />
    </CodesContext.Provider>
  );
};

const OveView: React.FC = () => {
  type Age = { years: number; months: number };

  const oveMaxAge = 69;
  const pensionMaxAge = 70;

  const {
    t,
    i18n: { language },
  } = useTranslation();
  const { search } = useLocation();
  const query = parseQuery(search);
  // Allow specifying year for e.g. repeatable Robot testing and manual testing:
  const calculationYear =
    Number.parseInt(query.year) || new Date().getFullYear();
  const currentMonth = new Date().getMonth();
  const validation = useValidation();

  const { yearConstants, pensionEstimate, index } = useContext(CodesContext);

  const resultsSection = useRef<HTMLDivElement | null>(null);

  // Inputs
  const [model, setModel] = useState<OveModel>({
    accruedPension: 0,
    birthMonth: 0,
    birthYear: 0,
    income: 0,
    incomeDevelopment: "projected",
    pensionAgeMonth: 0,
    pensionAgeYear: 0,
    priceConversion: true,
    continuesWorking: true,
    incomeWithOve: 0,
    isExceptionalLifetimeFactorUsed: false,
    ovePercentage: 0.5,
    oveAgeYear: 0,
    oveAgeMonth: 0,
  });
  const [isInRelationship, setIsInRelationship] = useState<boolean>(true); // Affects Kela pension
  const [oveAgeYear, setOveAgeYear] = useState<number | undefined>();
  const [oveAgeMonth, setOveAgeMonth] = useState<number | undefined>();
  const [pensionAgeYear, setPensionAgeYear] = useState<number | undefined>();
  const [pensionAgeMonth, setPensionAgeMonth] = useState<number | undefined>();

  // Results
  const [calcResult, setCalcResult] = useState<OveResult | null>(null);
  const [kelaResult, setKelaResult] =
    useState<KelaPensionCalculationResult | null>(null);
  const [kelaResultWithoutOve, setKelaResultWithoutOve] =
    useState<KelaPensionCalculationResult | null>(null);

  const calculationContext: OveCalculationContext | null = useMemo(() => {
    return {
      calculationYear,
      calculationMonth: currentMonth, //0-based
      indexData: index,
      pensionEstimate,
    } as OveCalculationContext;
  }, [index, pensionEstimate, currentMonth, calculationYear]);

  const currentAge = useMemo((): Age | undefined => {
    if (!calculationContext?.calculationYear) return undefined;
    let years = calculationContext.calculationYear - model.birthYear;
    let months = currentMonth - model.birthMonth; //0-based

    if (months < 0) {
      years -= 1;
      months += 12;
    }

    return { years, months };
  }, [
    calculationContext?.calculationYear,
    model.birthYear,
    model.birthMonth,
    currentMonth,
  ]);

  // This is only helper, returning years and months
  const lowestPensionAge: Age | undefined = useMemo(() => {
    if (calcResult?.lowestPensionAgeYear === undefined) return undefined;
    return {
      years: calcResult.lowestPensionAgeYear,
      months: calcResult.lowestPensionAgeMonth,
    };
  }, [calcResult?.lowestPensionAgeYear, calcResult?.lowestPensionAgeMonth]);

  const compareAges = (age1: Age, age2: Age): number =>
    age1.years * 12 + age1.months - (age2.years * 12 + age2.months) || 0;

  const getKelaModel = (
    oveResult: OveResult,
    birthYear: number,
    inRelationship: boolean,
    priceConversion: boolean,
  ): KelaModel => {
    return {
      earningsRelatedPension:
        oveResult.kertynytElakeIlmanvarhennusvahennystaJaIlmanLykkayskorotusta,
      earningsRelatedPensionAndIncreaseForLateRetirement:
        oveResult.pensionWithOve,
      inRelationship,
      birthYear: birthYear,
      pensionAgeYear: oveResult.pensionAgeYear, //These need to be from result
      pensionAgeMonth: oveResult.pensionAgeMonth,
      priceConversion,
      priceLevelIncrease: oveResult.priceLevelIncrease,
    };
  };

  // Price level options are displayed if the persons lowest OVE pension year
  // is the same or lower than the calculation year (current year in normal UI use)
  const showPriceLevelOptions = useMemo(() => {
    if (!calculationYear || calcResult?.calculatedOveAgeYear === undefined) {
      return false;
    }
    // What is the date (year and month) when person could start OVE pension
    // Note that pension can be started next month after person turns to lowest OVE age
    const lowestOveDate = calculateTargetDateForNextMonth(
      model.birthYear,
      model.birthMonth,
      calcResult?.calculatedOveAgeYear,
      calcResult?.calculatedOveAgeMonth,
    );
    return lowestOveDate.year <= calculationYear;
  }, [
    calculationYear,
    model.birthYear,
    model.birthMonth,
    calcResult?.calculatedOveAgeYear,
    calcResult?.calculatedOveAgeMonth,
  ]);

  // This should be called only from Calculate button click
  const calculate = (submittedModel: OveModel): void => {
    const result = calculateOve({ ...submittedModel }, calculationContext); //Use copy of model, calculatePension modifies it
    if (result) {
      setCalcResult(result);
      calculateKela(
        result,
        isInRelationship,
        submittedModel.priceConversion,
        submittedModel.birthYear,
      );
      // calculateOve returns initial values for OVE and pension age, display them
      setOveAgeYear(result.oveAgeYear);
      setOveAgeMonth(result.oveAgeMonth);
      setPensionAgeYear(result.pensionAgeYear);
      setPensionAgeMonth(result.pensionAgeMonth);
      scrollTo(resultsSection);
      resultsSection.current?.focus();
    }
  };

  // This is called after initial calculation, when changing age or price conversion inputs
  const reCalculateOve = ({
    newOveAgeYear,
    newOveAgeMonth,
    newPensionAgeYear,
    newPensionAgeMonth,
    newPriceConversion,
  }: {
    newOveAgeYear?: number;
    newOveAgeMonth?: number;
    newPensionAgeYear?: number;
    newPensionAgeMonth?: number;
    newPriceConversion?: boolean;
  }) => {
    // At this point, all values should be set, 0 is only for fallback for TypeScript compile
    let updatedOveAgeYear = newOveAgeYear ?? oveAgeYear ?? 0;
    let updatedOveAgeMonth = newOveAgeMonth ?? oveAgeMonth ?? 0;
    let updatedPensionAgeYear = newPensionAgeYear ?? pensionAgeYear ?? 0;
    let updatedPensionAgeMonth = newPensionAgeMonth ?? pensionAgeMonth ?? 0;
    let updatedPriceConversion =
      newPriceConversion ?? model.priceConversion ?? true;

    // Check pension max age, month selection cannot exceed 0
    if (updatedPensionAgeYear === pensionMaxAge) {
      updatedPensionAgeMonth = 0;
    }

    if (
      compareAges(
        { years: updatedOveAgeYear, months: updatedOveAgeMonth },
        { years: updatedPensionAgeYear, months: updatedPensionAgeMonth },
      ) >= 0
    ) {
      // Pension age is same or earlier than OVE age. Automatically set pension age one month after OVE age
      if (updatedOveAgeMonth === 11) {
        // OVE age is max month 11, set pension age to next year
        updatedPensionAgeYear = updatedOveAgeYear + 1;
        updatedPensionAgeMonth = 0;
      } else {
        updatedPensionAgeYear = updatedOveAgeYear;
        updatedPensionAgeMonth = updatedOveAgeMonth + 1;
      }
    }

    const updatedModel = {
      ...model,
      oveAgeYear: updatedOveAgeYear,
      oveAgeMonth: updatedOveAgeMonth,
      pensionAgeYear: updatedPensionAgeYear,
      pensionAgeMonth: updatedPensionAgeMonth,
      priceConversion: updatedPriceConversion,
    };
    const result = calculateOve({ ...updatedModel }, calculationContext); //Use copy of model, calculatePension modifies it
    if (result) {
      setCalcResult(result);
      // These should be basically the input, but in some cases calculateOve can modify them
      // TODO: See if this step is needed and see if calculation method can be simplified
      setOveAgeYear(result.oveAgeYear);
      setOveAgeMonth(result.oveAgeMonth);
      setPensionAgeYear(result.pensionAgeYear);
      setPensionAgeMonth(result.pensionAgeMonth);
      calculateKela(
        result,
        isInRelationship,
        updatedPriceConversion,
        updatedModel.birthYear,
      );
    }
  };

  // Calculate Kela pension if pensioner will be at least 55 years at the end of this year
  const calculateKela = (
    oveResult: OveResult,
    isInRelationship: boolean,
    priceConversion: boolean,
    birthYear: number,
  ) => {
    if (calculationYear - birthYear < 55) {
      //Reset these, no Kela pension to display
      setKelaResult(null);
      setKelaResultWithoutOve(null);
      return;
    }
    const kelaCalculationContext = {
      lowestPensionAgeYear: oveResult.lowestPensionAgeYear,
      lowestPensionAgeMonth: oveResult.lowestPensionAgeMonth,
      calculationYear: calculationContext.calculationYear,
      yearConstants,
      indexData: index,
    } as KelaCalculationContext;
    // First, calculate if Kela pension is given when isInRelationShip is false, i.e. living alone, Kela pension is displayed then
    const kelaModel = getKelaModel(
      oveResult,
      birthYear,
      false,
      priceConversion,
    );
    const kelaPensionWhenLivingAlone = calculateKelaPension(
      kelaModel,
      kelaCalculationContext,
    );
    if (kelaPensionWhenLivingAlone.pension > 0) {
      if (!isInRelationship) {
        setKelaResult(kelaPensionWhenLivingAlone);
      } else {
        kelaModel.inRelationship = true;
        setKelaResult(calculateKelaPension(kelaModel, kelaCalculationContext));
      }
      // Calculate Kela pension without OVE. same calculation but different input values
      kelaModel.earningsRelatedPension =
        oveResult.elakeIlmanLykkayskorotustaMuuttumatonPalkka;
      kelaModel.earningsRelatedPensionAndIncreaseForLateRetirement =
        oveResult.pensionWithoutOve;
      setKelaResultWithoutOve(
        calculateKelaPension(kelaModel, kelaCalculationContext),
      );
    } else {
      //Reset these, no Kela pension to display
      setKelaResult(null);
      setKelaResultWithoutOve(null);
    }
  };

  // Return minimum selectable month if year is minSelectableYear.
  // If maxAgeYear is same as year, return -1 (value is not used) and in other cases return 0 (no month option is disabled)
  const getMinimumSelectableMonth = (
    year: number,
    minSelectableAgeYear: number,
    minSelectableAgeMonth: number,
    maxAgeYear?: number,
  ): number => {
    if (year === maxAgeYear) {
      return -1;
    } else if (year === minSelectableAgeYear) {
      return minSelectableAgeMonth;
    }
    return 0;
  };

  const buildYearlyMonthOptions = (
    minSelectableAgeYear: number,
    minSelectableAgeMonth: number,
    yearOptions: { value: number; label: string }[],
    maxAgeYear?: number,
  ) => {
    const result: Record<
      number,
      { value: number; label: string; isDisabled: boolean }[]
    > = {};
    yearOptions.forEach((yearOption) => {
      const year = yearOption.value;
      const minimumSelectableMonth = getMinimumSelectableMonth(
        year,
        minSelectableAgeYear,
        minSelectableAgeMonth,
        maxAgeYear,
      );
      result[year] = Array.from({ length: 12 }, (_, i) => ({
        value: i,
        label: i.toString(),
        // With maxAgeYear, only 0 month is selectable, with other years all months before largestMonthDisabled
        isDisabled: year === maxAgeYear ? i !== 0 : i < minimumSelectableMonth,
      }));
    });
    return result;
  };

  // Retirement, i.e. pension age options need to re-calculated every time OVE or pension age changes according to chosen inputs or
  // after initial calculation
  const { retirementAgeYearOptions, retirementMonthOptions } = useMemo(() => {
    if (
      currentAge === undefined ||
      lowestPensionAge === undefined ||
      !oveAgeYear || // initially 0, no need to calculate then
      oveAgeMonth === undefined ||
      !pensionAgeYear || // initially 0, no need to calculate then
      pensionAgeMonth === undefined
    ) {
      return {
        retirementAgeYearOptions: rangeClosed(63, pensionMaxAge).map((y) => ({
          value: y,
          label: y + "",
        })),
        retirementMonthOptions: [],
      };
    } else {
      let retirementAgeYearOptions;
      let minSelectedAgeYear;
      let minSelectableAgeMonth;
      const oveAge = { years: oveAgeYear, months: oveAgeMonth };
      if (compareAges(lowestPensionAge, oveAge) > 0) {
        // Lowest pension age is over OVE age, display pension age and month options from the lowest pension age
        retirementAgeYearOptions = rangeClosed(
          lowestPensionAge.years,
          pensionMaxAge,
        ).map((y) => ({ value: y, label: y + "" }));
        minSelectedAgeYear = lowestPensionAge.years;
        minSelectableAgeMonth = lowestPensionAge.months;
      } else {
        // Lowest pension age is same or over pension age. Restrict available year and month options for according to OVE age
        minSelectableAgeMonth = oveAgeMonth === 11 ? 0 : oveAgeMonth + 1;
        // Handle year change, e.g. if ove age is 66 y 11 m, pension age should be 67 y 0 m and year options include 67-70
        retirementAgeYearOptions = rangeClosed(
          oveAgeMonth === 11 ? oveAgeYear + 1 : oveAgeYear,
          pensionMaxAge,
        ).map((y) => ({ value: y, label: y + "" }));
        minSelectedAgeYear = oveAgeYear;
      }
      const retirementMonthOptions: any = buildYearlyMonthOptions(
        minSelectedAgeYear,
        minSelectableAgeMonth,
        retirementAgeYearOptions,
        pensionMaxAge,
      );
      return { retirementAgeYearOptions, retirementMonthOptions };
    }
  }, [
    currentAge,
    lowestPensionAge,
    oveAgeYear,
    oveAgeMonth,
    pensionAgeYear,
    pensionAgeMonth,
  ]);

  // OVE age year and month options are calculated for calculated ove age and current age. They are not dependent on pension age
  const { oveAgeYearOptions, oveMonthOptions } = useMemo(() => {
    if (
      currentAge === undefined ||
      calcResult?.calculatedOveAgeYear === undefined ||
      calcResult?.calculatedOveAgeMonth === undefined
    ) {
      return {
        oveAgeYearOptions: rangeClosed(63, oveMaxAge).map((y) => ({
          value: y,
          label: y + "",
        })),
        oveMonthOptions: [],
      };
    } else {
      let ageYear;
      let ageMonth;
      let oveAgeYearOptions;
      if (
        compareAges(currentAge, {
          years: calcResult?.calculatedOveAgeYear,
          months: calcResult?.calculatedOveAgeMonth,
        }) > 0
      ) {
        oveAgeYearOptions = rangeClosed(currentAge.years, oveMaxAge).map(
          (y) => ({ value: y, label: y + "" }),
        );
        ageYear = currentAge.years;
        ageMonth = currentAge.months;
      } else {
        oveAgeYearOptions = rangeClosed(
          calcResult.calculatedOveAgeYear,
          oveMaxAge,
        ).map((y) => ({ value: y, label: y + "" }));
        ageYear = calcResult.calculatedOveAgeYear;
        ageMonth = calcResult.calculatedOveAgeMonth;
      }
      return {
        oveAgeYearOptions,
        oveMonthOptions: buildYearlyMonthOptions(
          ageYear,
          ageMonth,
          oveAgeYearOptions,
        ),
      };
    }
  }, [
    currentAge,
    calcResult?.calculatedOveAgeYear,
    calcResult?.calculatedOveAgeMonth,
  ]);

  const amount = (n: number) => localeEuroSumOrZero(language, n);

  return (
    <>
      <Container className="calculator-container content-container">
        <h1>{t("ove-laskuri.calculatorTitle")}</h1>
        <p className="ingress">{t("ove-laskuri.ingress1")}</p>
        <p className="ingress">{t("ove-laskuri.ingress2")}</p>
        <p className="ingress">{t("ove-laskuri.ingress3")}</p>
      </Container>
      <div className="form-container py-4">
        <Container className="content-container">
          <Row>
            <Col md={12}>
              <OveForm
                year={calculationYear}
                onClear={() => {}}
                onSubmit={(event, model) => {
                  event.preventDefault();
                  // Reset values set in result section by select inputs
                  setOveAgeYear(0);
                  setOveAgeMonth(0);
                  setPensionAgeYear(0);
                  setPensionAgeMonth(0);
                  setModel(model);
                  calculate(model);
                }}
                validation={validation}
              />
            </Col>
          </Row>
          <Row>
            {!validation.validationFeedback.length && model && (
              <Col
                id="results-region"
                role="region"
                aria-label={t("ove-laskuri.results.title")}
                md={12}
                className="mt-4"
                ref={resultsSection}
                tabIndex={-1}
              >
                {calcResult && (
                  <div className="results-container">
                    <h2>{t("ove-laskuri.results.title")}</h2>

                    <Form
                      noValidate
                      onSubmit={() => {}}
                      className="calculator-form"
                      id={"elake-laskuri-result-form"}
                    >
                      {showPriceLevelOptions ? (
                        <ResultsGroup
                          legend={t(
                            "elake-arvio-laskuri.results.priceLevel.label",
                          )}
                          className="mb-2"
                        >
                          <p>
                            {t(
                              "elake-arvio-laskuri.results.priceLevel.explanation",
                            )}
                          </p>
                          <HelpTextAccordion fieldName="elake-arvio-laskuri.results.priceLevel" />
                          <div className="radio-group-wrapper">
                            <RadioInput
                              fieldName="elake-arvio-laskuri.results.priceLevel"
                              ariaDescribedBy="elake-arvio-laskuri.results.priceLevel-help"
                              value={
                                model.priceConversion
                                  ? "currentYear"
                                  : "nominal"
                              }
                              options={[
                                {
                                  value: "currentYear",
                                  label: t(
                                    "elake-arvio-laskuri.results.priceLevel.currentYearLevel",
                                  ),
                                },
                                {
                                  value: "nominal",
                                  label: t(
                                    "elake-arvio-laskuri.results.priceLevel.nominalPriceLevel",
                                  ),
                                },
                              ]}
                              onChange={(value: string) => {
                                const newPriceConversion =
                                  value === "currentYear";
                                setModel((prev) => {
                                  const updatedModel = {
                                    ...prev,
                                    priceConversion: newPriceConversion,
                                  };
                                  reCalculateOve({ newPriceConversion });
                                  return updatedModel;
                                });
                              }}
                            />
                          </div>
                        </ResultsGroup>
                      ) : (
                        <p>
                          {t(
                            "elake-arvio-laskuri.results.priceLevelInformation",
                          )}
                        </p>
                      )}

                      <p className="result-paragraph-with-help">
                        {t(
                          "ove-laskuri.results.calculatedOvePensionAge.label",
                          {
                            years: calcResult.calculatedOveAgeYear,
                            months: calcResult.calculatedOveAgeMonth,
                          },
                        )}
                      </p>
                      <HelpTextAccordion
                        fieldName={
                          "ove-laskuri.results.calculatedOvePensionAge"
                        }
                      />

                      <ResultsGroup
                        legend={t("ove-laskuri.results.oveRetirementAge.label")}
                      >
                        <div className="side-by-side-container">
                        <LargeSelectInput<number>
                          fieldName="elake-arvio-laskuri.results.pensionAge.years"
                          label={
                            <h3 className="error-label">
                              {t(
                                "elake-arvio-laskuri.results.pensionAge.years.label",
                              )}
                            </h3>
                          }
                          options={oveAgeYearOptions}
                          value={oveAgeYear}
                          setValue={(selected) => {
                            if (selected?.value !== undefined) {
                              setOveAgeYear(selected.value);
                              startTransition(() => {
                                reCalculateOve({
                                  newOveAgeYear: selected.value,
                                });
                              });
                            }
                          }}
                        />
                        <LargeSelectInput<number>
                          fieldName="elake-arvio-laskuri.results.pensionAge.months"
                          label={
                            <span className="error-label">
                              {t(
                                "elake-arvio-laskuri.results.pensionAge.months.label",
                              )}
                            </span>
                          }
                          options={
                            oveAgeYear && oveMonthOptions[oveAgeYear]
                              ? oveMonthOptions[oveAgeYear]
                              : []
                          }
                          value={oveAgeMonth}
                          setValue={(selected) => {
                            if (selected?.value !== undefined) {
                              setOveAgeMonth(selected.value);
                              startTransition(() => {
                                reCalculateOve({
                                  newOveAgeMonth: selected.value,
                                });
                              });
                            }
                          }}
                        />
                      </div>
                      </ResultsGroup>

                      <p>
                        {t("ove-laskuri.results.oveRetirementMonthAndYear", {
                          month: calcResult?.elakeKuukausiOVEtodellinen,
                          year: calcResult?.elakeVuosiOVEtodellinen,
                        })}
                      </p>

                      <hr className="grey-divider" aria-hidden="true" />
                      {model.priceConversion ? (
                        <h2>{t("ove-laskuri.results.ovePension.title")}</h2>
                      ) : (
                        <h2>
                          {t("ove-laskuri.results.ovePension.titleWithYear", {
                            year: calcResult.elakeVuosiOVEtodellinen,
                          })}
                        </h2>
                      )}
                      {calcResult.ovePension !== undefined && (
                        <CalculationResult fieldName="ove-laskuri.results.ovePension">
                          <>{amount(calcResult.ovePension)}</>
                        </CalculationResult>
                      )}
                      {calcResult?.OVEvarhennusAika > 0 && (
                        <p>
                          {t(
                            "ove-laskuri.results.ovePension.varhennusAikaPositive",
                            {
                              months: calcResult?.OVEvarhennusAika,
                              reductionPercentage: toPercentage(
                                calcResult?.OVEvarhennusVahennys,
                              ),
                            },
                          )}
                        </p>
                      )}
                      {calcResult?.OVEvarhennusAika < 0 && (
                        <p>
                          {t(
                            "ove-laskuri.results.ovePension.varhennusAikaNegative",
                            {
                              months: -calcResult?.OVEvarhennusAika,
                              reductionPercentage: toPercentage(
                                calcResult?.OVEvarhennusVahennys.slice(1),
                              ),
                            },
                          )}
                        </p>
                      )}
                      {calcResult?.OVEvarhennusvahennysEUR > 0 && (
                        <p className="result-paragraph-with-help">
                          {t(
                            "ove-laskuri.results.ovePension.varhennusVahennysPositive",
                            {
                              euroSum: amount(
                                calcResult?.OVEvarhennusvahennysEUR,
                              ),
                            },
                          )}
                        </p>
                      )}
                      {calcResult?.OVEvarhennusvahennysEUR < 0 && (
                        <p className="result-paragraph-with-help">
                          {t(
                            "ove-laskuri.results.ovePension.varhennusVahennysNegative",
                            {
                              euroSum: amount(
                                -calcResult?.OVEvarhennusvahennysEUR,
                              ),
                            },
                          )}
                        </p>
                      )}
                      <HelpTextAccordion
                        fieldName={
                          "ove-laskuri.results.ovePension.varhennusVahennysHelp"
                        }
                      />

                      <p className="result-paragraph-with-help">
                        {t(
                          "ove-laskuri.results.ovePension.lifetimeFactor.label",
                        )}
                      </p>
                      <HelpTextAccordion
                        fieldName={
                          "ove-laskuri.results.ovePension.lifetimeFactor"
                        }
                      />

                      {!model.priceConversion &&
                        calcResult.elakeVuosiOVEtodellinen ===
                          calculationYear &&
                        calcResult.OVEelakeSeurVuosi !== undefined && (
                          <div className="mt-4 mb-0">
                            <hr className="grey-divider" aria-hidden="true" />
                            <h2>{t("ove-laskuri.results.priceLevel.title")}</h2>
                            <CalculationResult
                              fieldName="ove-laskuri.results.priceLevel"
                              labelExtra={
                                calcResult.elakeVuosiOVEtodellinen + 1 + ""
                              }
                            >
                              <>{amount(calcResult.OVEelakeSeurVuosi)}</>
                            </CalculationResult>

                            <p>
                              {t("ove-laskuri.results.priceLevel.explanation")}
                            </p>
                          </div>
                        )}

                      <hr className="grey-divider" aria-hidden="true" />
                      <h2>{t("ove-laskuri.results.pensionAfterOve.title")}</h2>

                      {kelaResult && (
                        <>
                          <h3>
                            {t(
                              "elake-arvio-laskuri.results.kela.livingWithSpouse.label",
                            )}
                          </h3>
                          <HelpTextAccordion
                            fieldName={
                              "elake-arvio-laskuri.results.kela.livingWithSpouse"
                            }
                          />
                          <div className="radio-group-wrapper">
                            <RadioInput
                              fieldName={
                                "elake-arvio-laskuri.results.kela.livingWithSpouse"
                              }
                              ariaDescribedBy="elake-arvio-laskuri.results.kela.livingWithSpouse-help"
                              options={[
                                {
                                  value: "true",
                                  label: t(
                                    "elake-arvio-laskuri.results.kela.livingWithSpouse.withSpouse",
                                  ),
                                },
                                {
                                  value: "false",
                                  label: t(
                                    "elake-arvio-laskuri.results.kela.livingWithSpouse.alone",
                                  ),
                                },
                              ]}
                              value={isInRelationship.toString()} // Convert boolean to string for selection
                              onChange={(value) => {
                                const newValue = value === "true";
                                setIsInRelationship(newValue);
                                calculateKela(
                                  calcResult,
                                  newValue,
                                  model.priceConversion,
                                  model.birthYear,
                                );
                              }}
                            />
                          </div>
                        </>
                      )}

                      <p className="result-paragraph-with-help">
                        {t(
                          "elake-arvio-laskuri.results.lowestPensionAge.label",
                          {
                            years: lowestPensionAge?.years,
                            months: lowestPensionAge?.months,
                          },
                        )}
                      </p>
                      <HelpTextAccordion fieldName="elake-arvio-laskuri.results.lowestPensionAge" />

                      <ResultsGroup
                        legend={t(
                          "elake-arvio-laskuri.results.retirementAge.label",
                        )}
                      >
                        <div className="side-by-side-container">
                        <LargeSelectInput<number>
                          fieldName="elake-arvio-laskuri.results.pensionAge.years"
                          label={
                            <span className="error-label">
                              {t(
                                "elake-arvio-laskuri.results.pensionAge.years.label",
                              )}
                            </span>
                          }
                          options={retirementAgeYearOptions}
                          value={pensionAgeYear}
                          setValue={(selected) => {
                            if (selected?.value !== undefined) {
                              setPensionAgeYear(selected.value);
                              startTransition(() => {
                                reCalculateOve({
                                  newPensionAgeYear: selected.value,
                                });
                              });
                            }
                          }}
                        />
                        <LargeSelectInput<number>
                          fieldName="elake-arvio-laskuri.results.pensionAge.months"
                          label={
                            <span className="error-label">
                              {t(
                                "elake-arvio-laskuri.results.pensionAge.months.label",
                              )}
                            </span>
                          }
                          options={
                            pensionAgeYear &&
                            retirementMonthOptions[pensionAgeYear]
                              ? retirementMonthOptions[pensionAgeYear]
                              : []
                          }
                          value={pensionAgeMonth}
                          setValue={(selected) => {
                            if (selected?.value !== undefined) {
                              setPensionAgeMonth(selected.value);
                              startTransition(() => {
                                reCalculateOve({
                                  newPensionAgeMonth: selected.value,
                                });
                              });
                            }
                          }}
                        />
                      </div>
                      </ResultsGroup>

                      <p>
                        {t(
                          "elake-arvio-laskuri.results.retirementMonthAndYear",
                          {
                            month:
                              language === "en"
                                ? getMonthName(calcResult?.pensionMonth, "en")
                                : calcResult?.pensionMonth,
                            year: calcResult?.pensionYear,
                          },
                        )}
                      </p>

                      {calcResult.pensionWithOve !== undefined && (
                        <CalculationResult fieldName="ove-laskuri.results.pensionWithOve">
                          <>{amount(calcResult.pensionWithOve)}</>
                        </CalculationResult>
                      )}

                      {kelaResult && (
                        <>
                          <CalculationResult fieldName="elake-arvio-laskuri.results.kela.pension">
                            <>{amount(kelaResult.pension)}</>
                          </CalculationResult>
                          <CalculationResult fieldName="elake-arvio-laskuri.results.kela.fullPension">
                            <>
                              {amount(
                                kelaResult.pension + calcResult.pensionWithOve,
                              )}
                            </>
                          </CalculationResult>
                        </>
                      )}

                      {calcResult?.OVEelakeVarhennusvahennys > 0 && (
                        <p className="result-paragraph-with-help">
                          {t(
                            "ove-laskuri.results.pensionAfterOve.varhennusVahennysPositive",
                            {
                              euroSum: amount(
                                calcResult?.OVEelakeVarhennusvahennys,
                              ),
                            },
                          )}
                        </p>
                      )}
                      {calcResult?.OVEelakeVarhennusvahennys < 0 && (
                        <p className="result-paragraph-with-help">
                          {t(
                            "ove-laskuri.results.pensionAfterOve.varhennusVahennysNegative",
                            {
                              euroSum: amount(
                                -calcResult?.OVEelakeVarhennusvahennys,
                              ),
                            },
                          )}
                        </p>
                      )}
                      <HelpTextAccordion fieldName="ove-laskuri.results.pensionAfterOve.varhennusVahennysHelp" />

                      {calcResult?.lykkayskorotusVE > 0 && (
                        <p>
                          {t(
                            "ove-laskuri.results.pensionAfterOve.lykkayskorotus",
                            {
                              euroSum: amount(calcResult?.lykkayskorotusVE),
                            },
                          )}
                        </p>
                      )}

                      <p>
                        {t("elake-arvio-laskuri.results.lifetimeFactor.label", {
                          lifetimeFactor: calcResult?.lifetimeFactor
                            .toString()
                            .replace(".", ","),
                        })}
                      </p>

                      <hr className="grey-divider" aria-hidden="true" />
                      <h2>
                        {t("ove-laskuri.results.pensionWithoutOve.title")}
                      </h2>
                      <p className="ingress">
                        {t("ove-laskuri.results.pensionWithoutOve.ingress1")}
                      </p>
                      {calcResult.pensionWithoutOve !== undefined && (
                        <CalculationResult fieldName="elake-arvio-laskuri.results.pension">
                          <>{amount(calcResult.pensionWithoutOve)}</>
                        </CalculationResult>
                      )}

                      {kelaResultWithoutOve && (
                        <>
                          <CalculationResult fieldName="elake-arvio-laskuri.results.kela.pension">
                            <>{amount(kelaResultWithoutOve.pension)}</>
                          </CalculationResult>
                          <CalculationResult fieldName="elake-arvio-laskuri.results.kela.fullPension">
                            <>
                              {amount(
                                kelaResultWithoutOve.pension +
                                  calcResult.pensionWithoutOve,
                              )}
                            </>
                          </CalculationResult>
                        </>
                      )}
                    </Form>
                  </div>
                )}
              </Col>
            )}
          </Row>
        </Container>
      </div>
    </>
  );
};

interface FormProps {
  year: number;
  onSubmit: (
    event: React.SyntheticEvent<HTMLFormElement>,
    model: OveModel,
  ) => void;
  onClear: () => void;
  validation: Validation;
}

const OveForm: React.FC<FormProps> = ({
  year,
  onSubmit,
  onClear,
  validation,
}) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const topSection = useRef<HTMLDivElement | null>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const errorSummary = useRef<HTMLDivElement>(null);
  useValidationOnLanguageChange(
    formRef,
    validation.validateForm,
    validation.validated,
  );

  const [formModel, setFormModel] = useState<OveModel>({
    accruedPension: 0,
    birthMonth: 0,
    birthYear: 0,
    income: 0,
    incomeDevelopment: "projected",
    pensionAgeMonth: 0,
    pensionAgeYear: 0,
    priceConversion: true,
    continuesWorking: true,
    incomeWithOve: 0,
    isExceptionalLifetimeFactorUsed: false,
    ovePercentage: 0.5,
    oveAgeYear: 0,
    oveAgeMonth: 0,
  });

  const { accruedPensions } = useContext(CodesContext);
  const medianAccruedPension = useMemo(() => {
    if (!accruedPensions || !formModel.birthYear) {
      return undefined;
    }
    const { birthYear } = formModel;
    const accrued = accruedPensionsValue(
      formModel.birthYear,
      "all",
      accruedPensions,
    );
    return accrued
      ? t("elake-arvio-laskuri.medianAccruedPension", { birthYear, accrued })
      : undefined;
  }, [accruedPensions, formModel.birthYear]);

  const handleSubmit = (event: React.SyntheticEvent<HTMLFormElement>) => {
    const validateResult = validation.validate(event);
    if (validateResult) {
      // Reset these after "Calculate is clicked"
      formModel.pensionAgeYear = 0;
      formModel.pensionAgeMonth = 0;
      formModel.priceConversion = true;
      onSubmit(event, formModel);
    } else {
      onClear();
      // Move focus to error summary
      if (errorSummary.current) {
        errorSummary.current.focus();
        setTimeout(() => {
          errorSummary.current?.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }, 100);
      } else {
        scrollTo(topSection);
      }
    }
  };

  const birthYearOptions = getBirthYearOptions(year, 66, 18).map((y) => ({
    value: y,
    label: y + "",
  }));
  const getMonthOptions = (locale: string) => {
    return Array.from({ length: 12 }, (_, i) => {
      const monthName = new Intl.DateTimeFormat(locale, {
        month: "long",
      }).format(new Date(2000, i));
      const capitalizedMonth =
        monthName.charAt(0).toUpperCase() + monthName.slice(1);
      return { value: i + 1, label: capitalizedMonth };
    });
  };

  return (
    <ValidationContext.Provider value={validation}>
      <div ref={topSection}></div>
      <Form
        ref={formRef}
        noValidate
        validated={validation.validated}
        onSubmit={handleSubmit}
        className="calculator-form"
        id={"elake-laskuri-form"}
      >
        <ValidationFeedback
          feedback={validation.validationFeedback}
          ref={errorSummary}
        />
        <p className="required-fields-info">
          <span className="required">*</span>&nbsp;
          {t("requiredFieldsInfo")}
        </p>
        <Row>
          <Col md={12}>
            <InputGroup
              hasValidation
              className={`${
                validation.validated ? "was-validated" : "was-not-validated"
              }`}
            >
              <LargeSelectInput<number>
                fieldName="ove-laskuri.birthYear"
                required
                showValidation={validation.validated}
                label={
                  <span className="error-label">
                    {t("ove-laskuri.birthYear.label")}
                    <span className="required">*</span>
                  </span>
                }
                helpTextAccordion={
                  <HelpTextAccordion fieldName="ove-laskuri.birthYear" />
                }
                options={birthYearOptions}
                value={formModel.birthYear}
                setValue={(selected) => {
                  if (selected?.value !== undefined) {
                    setFormModel((fm) => ({
                      ...fm,
                      birthYear: selected.value,
                    }));
                  }
                }}
              />
              <LargeSelectInput<number>
                fieldName="elake-arvio-laskuri.birthMonth"
                required
                showValidation={validation.validated}
                label={
                  <span className="error-label">
                    {t("elake-arvio-laskuri.birthMonth.label")}
                    <span className="required">*</span>
                  </span>
                }
                options={getMonthOptions(language)}
                value={formModel.birthMonth}
                setValue={(selected) => {
                  if (selected?.value !== undefined) {
                    setFormModel((fm) => ({
                      ...fm,
                      birthMonth: selected.value,
                    }));
                  }
                }}
              />
              <NumberInput
                fieldName={"ove-laskuri.income"}
                initialValue={formModel.income}
                min={0}
                max={999999999}
                onlyInts={true}
                required={true}
                onValidValueChange={(income?) => {
                  if (income !== undefined) {
                    setFormModel((fm) => ({ ...fm, income }));
                  }
                }}
              />
              <NumberInput
                fieldName={"elake-arvio-laskuri.accruedPension"}
                extraInformationField={medianAccruedPension}
                initialValue={formModel.accruedPension}
                min={0}
                max={999999999}
                onlyInts={true}
                required={true}
                onValidValueChange={(accruedPension?) => {
                  if (accruedPension !== undefined) {
                    setFormModel((fm) => ({ ...fm, accruedPension }));
                  }
                }}
              />
              <LargeSelectInput<number>
                fieldName="ove-laskuri.ovePercentage"
                label={
                  <span className="error-label">
                    {t("ove-laskuri.ovePercentage.label")}
                  </span>
                }
                helpTextAccordion={
                  <HelpTextAccordion fieldName="ove-laskuri.ovePercentage" />
                }
                options={[
                  {
                    value: 0.5,
                    label: t("ove-laskuri.ovePercentage.half"),
                  },
                  {
                    value: 0.25,
                    label: t("ove-laskuri.ovePercentage.quarter"),
                  },
                ]}
                value={formModel.ovePercentage}
                setValue={(selected) => {
                  if (selected?.value) {
                    setFormModel((fm) => ({
                      ...fm,
                      ovePercentage: selected.value,
                    }));
                  }
                }}
              />
              <NumberInput
                fieldName={"ove-laskuri.incomeWithOve"}
                initialValue={formModel.incomeWithOve}
                min={0}
                max={999999999}
                onlyInts={true}
                required={true}
                onValidValueChange={(incomeWithOve?) => {
                  if (incomeWithOve !== undefined) {
                    setFormModel((fm) => ({ ...fm, incomeWithOve }));
                  }
                }}
              />
              <LargeSelectInput
                fieldName="elake-arvio-laskuri.incomeDevelopment"
                required={false}
                label={
                  <span className="error-label">
                    {t("elake-arvio-laskuri.incomeDevelopment.label")}
                  </span>
                }
                helpTextAccordion={
                  <HelpTextAccordion fieldName="elake-arvio-laskuri.incomeDevelopment" />
                }
                options={[
                  {
                    value: "projected",
                    label: t("elake-arvio-laskuri.incomeDevelopment.projected"),
                  },
                  {
                    value: "static",
                    label: t("elake-arvio-laskuri.incomeDevelopment.static"),
                  },
                ]}
                value={formModel.incomeDevelopment}
                setValue={(selected) => {
                  if (selected?.value) {
                    setFormModel((fm) => ({
                      ...fm,
                      incomeDevelopment: selected.value,
                    }));
                  }
                }}
              />
            </InputGroup>
          </Col>
        </Row>
        <Row>
          <Col sm={6} className="mt-2">
            <Button
              type="submit"
              aria-controls="results-region"
              onClick={() => {
                trackEvent("Button", t("elake-arvio-laskuri.calculate"));
              }}
            >
              {t("elake-arvio-laskuri.calculate")}
            </Button>
          </Col>
        </Row>
      </Form>
    </ValidationContext.Provider>
  );
};

export default OveCalculator;
